import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

import { TOKENS } from '../theme';

// Deleted employee sessions stay here for 24h (EmployeeSessionRecoveryService)
// before the hourly purge job removes them for good. Same shape as the
// reading-session Recovery Safe in MasterRecordForm: Recover, or Delete
// Forever behind a type-DELETE confirm.

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function formatExpiresIn(expiresAt, now) {
  if (!expiresAt) return null;
  const end = new Date(expiresAt).getTime();
  if (isNaN(end)) return null;
  const diff = end - now;
  if (diff <= 0) return 'Expiring now';
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
}

function MetaRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ fontSize: 13, color: TOKENS.slate500 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: TOKENS.slate900,
          textAlign: 'right',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}

export default function EmployeeSessionRecoverySafe({
  deletedSessions,
  busyId,
  onRestore,
  onDeleteForever,
}) {
  // { type: 'restore' | 'deleteForever', entry }
  const [confirm, setConfirm] = useState(null);
  const [typed, setTyped] = useState('');
  // Re-render the "time left" chips once a minute.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  if (!deletedSessions || deletedSessions.length === 0) return null;

  const closeConfirm = () => {
    setConfirm(null);
    setTyped('');
  };
  const confirmOk =
    confirm?.type !== 'deleteForever' ||
    typed.trim().toUpperCase() === 'DELETE';
  const runConfirm = () => {
    if (!confirm || !confirmOk) return;
    const { type, entry } = confirm;
    closeConfirm();
    if (type === 'restore') onRestore(entry);
    else onDeleteForever(entry);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Inventory2OutlinedIcon sx={{ color: TOKENS.slate500 }} />
        <Typography
          sx={{ fontSize: 18, fontWeight: 700, color: TOKENS.slate900 }}
        >
          Recovery Safe
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 14, color: TOKENS.slate500, mt: 0.5, mb: 2 }}>
        Deleted employee sessions can be recovered within 24 hours before they
        are permanently removed.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(auto-fill, minmax(300px, 1fr))',
          },
        }}
      >
        {deletedSessions.map((d) => {
          const expiresIn = formatExpiresIn(d.expiresAt, now);
          const busy = !!busyId;
          return (
            <Box
              key={d.deletedId}
              sx={{
                background: '#fff',
                border: `1px solid ${TOKENS.slate200}`,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ p: 2, flex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: TOKENS.slate900,
                    }}
                    noWrap
                  >
                    {d.employeeName || 'Employee'}
                    {d.sessionId ? ` · #${d.sessionId}` : ''}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: '2px',
                      borderRadius: '6px',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: TOKENS.red700,
                      bgcolor: TOKENS.red100,
                      flexShrink: 0,
                    }}
                  >
                    Deleted
                  </Box>
                </Box>

                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}
                >
                  <MetaRow label="Clock in" value={d.clockIn} />
                  <MetaRow
                    label="Clock out"
                    value={d.open ? 'Still open' : d.clockOut}
                  />
                  <MetaRow label="Deleted by" value={d.deletedByName} />
                  <MetaRow label="Deleted at" value={formatDate(d.deletedAt)} />
                </Box>

                {expiresIn && (
                  <Box
                    sx={{
                      mt: 1.5,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: '999px',
                      bgcolor: TOKENS.amber100,
                      color: TOKENS.amber700,
                      fontSize: 12,
                      fontWeight: 600,
                      '& svg': { fontSize: 16 },
                    }}
                  >
                    <TimerOutlinedIcon />
                    {expiresIn}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  p: 1.5,
                  borderTop: `1px solid ${TOKENS.slate100}`,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  disabled={busy}
                  startIcon={busyId === d.deletedId ? null : <RestoreIcon />}
                  onClick={() => setConfirm({ type: 'restore', entry: d })}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  {busyId === d.deletedId ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    'Recover'
                  )}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={busy}
                  startIcon={<DeleteForeverIcon />}
                  onClick={() =>
                    setConfirm({ type: 'deleteForever', entry: d })
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  Delete Forever
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Dialog open={!!confirm} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirm?.type === 'deleteForever'
            ? 'Delete forever?'
            : 'Recover this session?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.type === 'deleteForever'
              ? 'This session will be removed from the recovery safe and can NOT be brought back.'
              : confirm?.entry?.open
              ? 'The session comes back with all its entries. It was still open when it was deleted, so the employee will be clocked in on it again.'
              : 'The session comes back with all its entries — transactions, bonuses, raffles, ticket-outs and points.'}
          </DialogContentText>
          {confirm?.type === 'deleteForever' && (
            <TextField
              autoFocus
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label='Type "DELETE" to confirm'
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfirm} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            color={confirm?.type === 'deleteForever' ? 'error' : 'primary'}
            disabled={!confirmOk}
            onClick={runConfirm}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {confirm?.type === 'deleteForever' ? 'Delete Forever' : 'Recover'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
